import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Group, DollarCircle, DatabaseBackup, WarningCircle, Settings, Search } from 'iconoir-react';
import '../admin.css';

interface PlatformStats {
  total_users: number;
  active_subscriptions: number;
  monthly_revenue: number;
  pending_invoices: number;
  total_storage_used_gb: number;
  subscriptions_by_period: { [key: string]: number };
}

interface User {
  id: number;
  name: string;
  email: string;
  company_name: string | null;
  created_at: string;
  subscription: {
    status: string;
    billing_period: string;
    current_period_end: string | null;
  };
  usage: {
    owned_boards: number;
    invited_editors: number;
    invited_visitors: number;
    storage_gb: number;
  };
  cost: {
    total: number;
    monthly_equivalent: number;
    billing_period: string;
  };
  tenant: {
    id: number | null;
    name: string | null;
  };
}

const UserAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users', {
          params: {
            status: statusFilter !== 'all' ? statusFilter : undefined,
          },
        }),
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const response = await api.get('/admin/users', {
        params: {
          search: searchTerm,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        },
      });
      setUsers(response.data.data);
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { label: string; color: string } } = {
      active: { label: 'Actif', color: '#00bf7e' },
      suspended: { label: 'Suspendu', color: '#e74c3c' },
      cancelled: { label: 'Annulé', color: '#95a5a6' },
      none: { label: 'Aucun', color: '#f39c12' },
    };
    return badges[status] || badges.none;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>Administration Ventilio</h1>
          <p>Gestion des utilisateurs et facturation</p>
        </div>
        <div className="admin-header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/admin/saas/plans')}
          >
            <Settings width={20} height={20} strokeWidth={2} />
            Plans d'abonnement
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/admin/saas/extended')}
          >
            <Settings width={20} height={20} strokeWidth={2} />
            Gestion avancée
          </button>
        </div>
      </div>

      {/* Statistiques globales */}
      {stats && (
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ backgroundColor: '#026AA7' }}>
              <Group width={32} height={32} strokeWidth={2} />
            </div>
            <div className="admin-stat-content">
              <div className="admin-stat-label">Utilisateurs</div>
              <div className="admin-stat-value">{stats.total_users}</div>
              <div className="admin-stat-detail">
                {stats.active_subscriptions} abonnements actifs
              </div>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ backgroundColor: '#00bf7e' }}>
              <DollarCircle width={32} height={32} strokeWidth={2} />
            </div>
            <div className="admin-stat-content">
              <div className="admin-stat-label">Revenu mensuel</div>
              <div className="admin-stat-value">{formatCurrency(stats.monthly_revenue)}</div>
              <div className="admin-stat-detail">
                {stats.subscriptions_by_period?.monthly || 0} mensuels / {stats.subscriptions_by_period?.yearly || 0} annuels
              </div>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ backgroundColor: '#f39c12' }}>
              <WarningCircle width={32} height={32} strokeWidth={2} />
            </div>
            <div className="admin-stat-content">
              <div className="admin-stat-label">Factures en attente</div>
              <div className="admin-stat-value">{stats.pending_invoices}</div>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ backgroundColor: '#9b59b6' }}>
              <DatabaseBackup width={32} height={32} strokeWidth={2} />
            </div>
            <div className="admin-stat-content">
              <div className="admin-stat-label">Stockage total</div>
              <div className="admin-stat-value">{stats.total_storage_used_gb.toFixed(2)} Go</div>
            </div>
          </div>
        </div>
      )}

      {/* Filtres et recherche */}
      <div className="admin-filters">
        <div className="admin-search">
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="admin-search-input"
          />
          <button className="btn btn-primary" onClick={handleSearch}>
            <Search width={18} height={18} strokeWidth={2} />
            Rechercher
          </button>
        </div>

        <div className="admin-filter-group">
          <label>Statut :</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="admin-select"
          >
            <option value="all">Tous</option>
            <option value="active">Actifs</option>
            <option value="suspended">Suspendus</option>
            <option value="cancelled">Annulés</option>
          </select>
        </div>
      </div>

      {/* Liste des utilisateurs */}
      <div className="admin-section">
        <h2>Utilisateurs ({users.length})</h2>
        
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Société</th>
                <th>Abonnement</th>
                <th>Tableaux</th>
                <th>Invités</th>
                <th>Stockage</th>
                <th>Coût mensuel</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const statusBadge = getStatusBadge(user.subscription.status);
                return (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar-small">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="user-name">{user.name}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{user.company_name || '-'}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: statusBadge.color }}
                      >
                        {statusBadge.label}
                      </span>
                      <div className="subscription-period">
                        {user.subscription.billing_period === 'yearly' ? 'Annuel' : 'Mensuel'}
                      </div>
                    </td>
                    <td className="text-center">{user.usage.owned_boards}</td>
                    <td className="text-center">
                      <div>{user.usage.invited_editors} éditeurs</div>
                      <div className="text-muted">{user.usage.invited_visitors} visiteurs</div>
                    </td>
                    <td className="text-center">{user.usage.storage_gb.toFixed(2)} Go</td>
                    <td className="text-right">
                      <div className="cost-cell">
                        {formatCurrency(user.cost.monthly_equivalent)}
                        {user.cost.billing_period === 'yearly' && (
                          <div className="text-muted">
                            ({formatCurrency(user.cost.total)}/an)
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigate(`/admin/saas/users/${user.id}`)}
                      >
                        Détails
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserAdminDashboard;
